<template>
  <div class="error-monitor">
    <!-- 动态网格背景 -->
    <div class="cyber-grid">
      <div class="grid-lines-h"></div>
      <div class="grid-lines-v"></div>
    </div>
    
    <!-- 扫描雷达效果 -->
    <div class="radar-scanner"></div>
    
    <!-- 数据概览仪表盘 -->
    <div class="monitor-dashboard">
      <div 
        v-for="tab in tabs" 
        :key="tab.type"
        class="dashboard-item"
        :class="{ active: activeTab === tab.type, warning: hasWarning(tab.type) }"
        @click="activeTab = tab.type"
      >
        <div class="dashboard-bg">
          <div class="scan-line"></div>
          <div class="hex-pattern"></div>
          <div class="corner tl"></div>
          <div class="corner tr"></div>
          <div class="corner bl"></div>
          <div class="corner br"></div>
        </div>
        <div class="dashboard-content">
          <div class="icon-wrapper">
            <div class="icon-pulse"></div>
            <div class="icon-glow"></div>
            <svg v-if="tab.type === 'device_online'" class="icon-svg" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.5" opacity="0.3"/>
              <circle cx="12" cy="12" r="6" stroke="currentColor" stroke-width="1.5" opacity="0.5"/>
              <circle cx="12" cy="12" r="2" fill="currentColor"/>
              <path d="M12 2v4M12 18v4M2 12h4M18 12h4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
              <path d="M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" opacity="0.5"/>
            </svg>
            <svg v-else-if="tab.type === 'device_error'" class="icon-svg" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2L2 20h20L12 2z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
              <path d="M12 9v4" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              <circle cx="12" cy="17" r="1" fill="currentColor"/>
              <path d="M12 2L2 20" stroke="currentColor" stroke-width="0.5" opacity="0.3"/>
              <path d="M12 2L22 20" stroke="currentColor" stroke-width="0.5" opacity="0.3"/>
            </svg>
            <svg v-else-if="tab.type === 'running_error'" class="icon-svg" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" stroke-width="1.5"/>
              <path d="M3 9h18M9 3v18" stroke="currentColor" stroke-width="1.5" opacity="0.3"/>
              <circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="1.5"/>
              <path d="M12 12l6 6M12 12l6-6M12 12l-6 6M12 12l-6-6" stroke="currentColor" stroke-width="1" opacity="0.5"/>
              <circle cx="6" cy="6" r="1.5" fill="currentColor" opacity="0.6"/>
              <circle cx="18" cy="6" r="1.5" fill="currentColor" opacity="0.6"/>
              <circle cx="6" cy="18" r="1.5" fill="currentColor" opacity="0.6"/>
              <circle cx="18" cy="18" r="1.5" fill="currentColor" opacity="0.6"/>
            </svg>
          </div>
          <div class="info-wrapper">
            <div class="label">{{ tab.labelKey ? langMap[tab.labelKey][lang] : tab.label }}</div>
            <div class="count-wrapper">
              <span class="count" :class="getCountClass(tab.type)">
                {{ getErrorCount(tab.type) }}
              </span>
              <span class="unit">{{ langMap.dashboard_unitCount[lang] }}</span>
            </div>
            <div class="progress-bar">
              <div class="progress-fill" :style="{ width: getProgressWidth(tab.type) }" :class="getCountClass(tab.type)"></div>
            </div>
          </div>
        </div>
        <div v-if="hasWarning(tab.type)" class="warning-badge">
          <div class="badge-pulse"></div>
          <svg class="warning-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2L2 20h20L12 2z" stroke="currentColor" stroke-width="2" stroke-linejoin="round" fill="none"/>
            <path d="M12 9v4" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/>
            <circle cx="12" cy="17" r="1.5" fill="currentColor"/>
          </svg>
        </div>
      </div>
    </div>

    <div class="monitor-content">
      <!-- 数据流动背景 -->
      <div class="data-stream">
        <div v-for="i in 15" :key="i" class="stream-line" :style="getStreamStyle(i)"></div>
      </div>
      
      <!-- 设备离线状态 -->
      <div v-if="activeTab === 'device_online'" class="error-list">
        <div v-if="errorData.device_online.length === 0" class="empty-state">
          <div class="empty-icon">
            <div class="hologram-ring"></div>
            <div class="hologram-ring delay-1"></div>
            <div class="hologram-ring delay-2"></div>
            <i class="el-icon-success"></i>
          </div>
          <p class="empty-text">{{ langMap.dashboard_noDeviceOffline[lang] }}</p>
          <div class="empty-subtext">SYSTEM STATUS: OPTIMAL</div>
        </div>
        <div 
          v-else
          v-for="item in errorData.device_online" 
          :key="item.id"
          class="error-item"
          :class="getStatusClass(item.status)"
        >
          <div class="item-border">
            <div class="border-corner tl"></div>
            <div class="border-corner tr"></div>
            <div class="border-corner bl"></div>
            <div class="border-corner br"></div>
          </div>
          <div class="glitch-effect"></div>
          <div class="hologram-overlay"></div>
          <div class="data-flow-line"></div>
          <div class="error-header">
            <div class="device-info">
              <div class="status-indicator" :class="item.status === 0 ? 'online' : 'offline'"></div>
              <span class="device-name">{{ getDeviceName(item) }}</span>
              <span class="device-id">{{ item.deviceId }}</span>
            </div>
            <div class="error-time">{{ formatTime(item.dataTime) }}</div>
          </div>
          <div class="error-details">
            <div class="detail-item">
              <span class="label">{{ langMap.dashboard_deviceOriginalName[lang] }}:</span>
              <span class="value">{{ item.deviceName || '--' }}</span>
            </div>
            <div class="detail-item">
              <span class="label">{{ langMap.dashboard_statusLabel[lang] }}:</span>
              <span class="value" :class="item.status === 0 ? 'status-online' : 'status-offline'">
                {{ item.status === 0 ? langMap.dashboard_online[lang] : langMap.dashboard_disconnected[lang] }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- 设备错误 -->
      <div v-if="activeTab === 'device_error'" class="error-list">
        <div v-if="errorData.device_error.length === 0" class="empty-state">
          <div class="empty-icon">
            <div class="hologram-ring"></div>
            <div class="hologram-ring delay-1"></div>
            <div class="hologram-ring delay-2"></div>
            <i class="el-icon-success"></i>
          </div>
          <p class="empty-text">{{ langMap.dashboard_noDeviceError[lang] }}</p>
          <div class="empty-subtext">SYSTEM STATUS: OPTIMAL</div>
        </div>
        <div 
          v-else
          v-for="item in errorData.device_error" 
          :key="item.id"
          class="error-item"
          :class="getLevelClass(item.errLevel)"
        >
          <div class="error-header">
            <div class="device-info">
              <div class="level-badge" :class="getLevelClass(item.errLevel)">
                {{ getLevelText(item.errLevel) }}
              </div>
              <span class="device-name">{{ getDeviceName(item) }}</span>
              <span class="error-code">{{ item.errCode }}</span>
            </div>
            <div class="header-actions">
              <div class="error-time">{{ formatTime(item.dataTime) }}</div>
              <div class="delete-btn" @click.stop="deleteDeviceError(item.id)" :title="langMap.dashboard_delete[lang]">
                <Icon type="md-trash" size="18" />
              </div>
            </div>
          </div>
          <div class="error-details">
            <div class="detail-item">
              <span class="label">{{ langMap.dashboard_deviceOriginalName[lang] }}:</span>
              <span class="value">{{ item.deviceName || '--' }}</span>
            </div>
            <div class="detail-item">
              <span class="label">{{ langMap.dashboard_errorDesc[lang] }}:</span>
              <span class="value">{{ getErrorDesc(item) }}</span>
            </div>
            <div class="detail-item">
              <span class="label">{{ langMap.dashboard_deviceIdLabel[lang] }}:</span>
              <span class="value">{{ item.deviceId }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 运行错误 -->
      <div v-if="activeTab === 'running_error'" class="error-list">
        <div v-if="errorData.running_error.length === 0" class="empty-state">
          <div class="empty-icon">
            <div class="hologram-ring"></div>
            <div class="hologram-ring delay-1"></div>
            <div class="hologram-ring delay-2"></div>
            <i class="el-icon-success"></i>
          </div>
          <p class="empty-text">{{ langMap.dashboard_noRunningError[lang] }}</p>
          <div class="empty-subtext">SYSTEM STATUS: OPTIMAL</div>
        </div>
        <div 
          v-else
          v-for="item in errorData.running_error" 
          :key="item.id"
          class="error-item"
          :class="getLevelClass(item.errLevel)"
        >
          <div class="error-header">
            <div class="device-info">
              <div class="level-badge" :class="getLevelClass(item.errLevel)">
                {{ getLevelText(item.errLevel) }}
              </div>
              <span class="device-name">{{ getDeviceName(item) }}</span>
              <span class="error-code">{{ item.errCode }}</span>
            </div>
            <div class="header-actions">
              <div class="error-time">{{ formatTime(item.dataTime) }}</div>
              <div class="delete-btn" @click.stop="deleteRunningError(item.id)" :title="langMap.dashboard_delete[lang]">
                <Icon type="md-trash" size="18" />
              </div>
            </div>
          </div>
          <div class="error-details">
            <div class="detail-item">
              <span class="label">{{ langMap.dashboard_deviceOriginalName[lang] }}:</span>
              <span class="value">{{ item.deviceName || '--' }}</span>
            </div>
            <div class="detail-item">
              <span class="label">{{ langMap.dashboard_errorDesc[lang] }}:</span>
              <span class="value">{{ getErrorDesc(item) }}</span>
            </div>
            <div class="detail-item">
              <span class="label">{{ langMap.dashboard_paramValueLabel[lang] }}:</span>
              <span class="value value-highlight">{{ item.value }}</span>
            </div>
            <div class="detail-item">
              <span class="label">{{ langMap.dashboard_deviceIdLabel[lang] }}:</span>
              <span class="value">{{ item.deviceId }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 自定义确认对话框 -->
    <div v-if="showDeleteConfirm" class="cyber-modal-overlay" @click="closeDeleteConfirm">
      <div class="cyber-modal" @click.stop>
        <div class="modal-border">
          <div class="border-corner tl"></div>
          <div class="border-corner tr"></div>
          <div class="border-corner bl"></div>
          <div class="border-corner br"></div>
        </div>
        <div class="modal-scan-line"></div>
        <div class="modal-header">
          <div class="header-icon">
            <div class="icon-pulse"></div>
            <Icon type="md-warning" size="32" />
          </div>
          <h3 class="header-title">{{ langMap.dashboard_warning[lang] }}</h3>
        </div>
        <div class="modal-content">
          <p class="content-text">{{ langMap.dashboard_confirmDelete[lang] }}</p>
        </div>
        <div class="modal-footer">
          <button class="cyber-btn cancel-btn" @click="closeDeleteConfirm">
            <span class="btn-bg"></span>
            <span class="btn-text">{{ langMap.dashboard_cancel[lang] }}</span>
          </button>
          <button class="cyber-btn confirm-btn" @click="confirmDelete">
            <span class="btn-bg"></span>
            <span class="btn-text">{{ langMap.dashboard_confirm[lang] }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import langMap from '@/utils/language';
import { mapState } from 'vuex';
import { getData } from '@/api/api';

export default {
  name: 'ErrorMonitor',
  props: {
    errorData: {
      type: Object,
      default: () => ({
        device_online: [],
        device_error: [],
        running_error: []
      })
    }
  },
  data() {
    return {
      langMap,
      activeTab: 'device_online',
      tabs: [
        { type: 'device_online', labelKey: 'dashboard_deviceOfflineStatus', icon: 'el-icon-connection' },
        { type: 'device_error', labelKey: 'dashboard_deviceErrorTab', icon: 'el-icon-warning-outline' },
        { type: 'running_error', labelKey: 'dashboard_runningErrorTab', icon: 'el-icon-cpu' }
      ],
      maxCount: 20, // 用于计算进度条
      showDeleteConfirm: false,
      deleteTargetId: null,
      deleteType: null // 'device' 或 'running'
    }
  },
  computed: {
    ...mapState({
      lang: state => state.app.language,
    })
  },
  methods: {
    getErrorCount(type) {
      // 对于设备离线状态，只计算离线设备数量
      if (type === 'device_online') {
        return this.errorData[type]?.filter(item => item.status !== 0).length || 0;
      }
      return this.errorData[type]?.length || 0;
    },
      hasWarning(type) {
        const count = this.getErrorCount(type);
        return count > 0;
      },
    getProgressWidth(type) {
      const count = this.getErrorCount(type);
      const percentage = Math.min((count / this.maxCount) * 100, 100);
      return `${percentage}%`;
    },
    getParticleStyle(index) {
      const delay = Math.random() * 5;
      const duration = 3 + Math.random() * 4;
      const x = Math.random() * 100;
      const y = Math.random() * 100;
      return {
        left: `${x}%`,
        top: `${y}%`,
        animationDelay: `${delay}s`,
        animationDuration: `${duration}s`
      };
    },
    getStreamStyle(index) {
      const delay = Math.random() * 3;
      const duration = 2 + Math.random() * 2;
      const height = 1 + Math.random() * 3;
      return {
        left: `${(index / 15) * 100}%`,
        animationDelay: `${delay}s`,
        animationDuration: `${duration}s`,
        height: `${height}px`
      };
    },
    getCountClass(type) {
      const count = this.getErrorCount(type);
      if (count === 0) return 'count-normal';
      return count > 0 ? 'count-error' : 'count-normal';
    },
    getDeviceName(item) {
      return item[`deviceName${this.lang === 'zh' ? 'Cn' : 'En'}`];
    },
    getErrorDesc(item) {
      return item[`errCode${this.lang === 'zh' ? 'Cn' : 'En'}`];
    },
    getLevelText(level) {
      const map = {
        0: this.langMap.dashboard_levelRecovered[this.lang],
        1: this.langMap.dashboard_levelWarn[this.lang],
        2: this.langMap.dashboard_levelError[this.lang],
        3: this.langMap.dashboard_levelFatal[this.lang]
      };
      return map[level] || this.langMap.dashboard_levelUnknown[this.lang];
    },
    getLevelClass(level) {
      const map = {
        0: 'level-recovered',
        1: 'level-warn',
        2: 'level-error',
        3: 'level-fatal'
      };
      return map[level] || '';
    },
    getStatusClass(status) {
      return status === 0 ? 'status-online' : 'status-offline';
    },
    formatTime(timestamp) {
      if (!timestamp) return '--';
      const date = new Date(timestamp);
      return date.toLocaleString('zh-CN', {
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
    },
    deleteDeviceError(id) {
      this.deleteTargetId = id;
      this.deleteType = 'device';
      this.showDeleteConfirm = true;
    },
    deleteRunningError(id) {
      this.deleteTargetId = id;
      this.deleteType = 'running';
      this.showDeleteConfirm = true;
    },
    closeDeleteConfirm() {
      this.showDeleteConfirm = false;
      this.deleteTargetId = null;
      this.deleteType = null;
    },
    async confirmDelete() {
      const id = this.deleteTargetId;
      const type = this.deleteType;
      this.closeDeleteConfirm();
      
      try {
        const apiUrl = type === 'device' 
          ? `notice-server/monitor/record/clear/deviceError/${id}`
          : `notice-server/monitor/record/clear/runningError/${id}`;
        
        const result = await getData(apiUrl, this);
        if (result.state === 1) {
          this.$Message.success(this.langMap.dashboard_deleteSuccess[this.lang]);
          // 直接从当前数据中删除该记录
          this.$emit('delete-record', { type, id });
        } else {
          this.$Message.error(result.msg || this.langMap.dashboard_deleteFailed[this.lang]);
        }
      } catch (error) {
        this.$Message.error(this.langMap.dashboard_deleteFailed[this.lang]);
      }
    }
  }
}
</script>

<style lang="less" scoped>
.error-monitor {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: 
    linear-gradient(135deg, rgba(0, 10, 20, 0.95) 0%, rgba(0, 30, 60, 0.9) 50%, rgba(0, 10, 20, 0.95) 100%),
    radial-gradient(circle at 20% 50%, rgba(0, 240, 255, 0.05) 0%, transparent 50%),
    radial-gradient(circle at 80% 50%, rgba(255, 0, 128, 0.05) 0%, transparent 50%);
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  box-shadow: 
    inset 0 0 100px rgba(0, 240, 255, 0.05),
    0 0 50px rgba(0, 0, 0, 0.5);
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: 
      repeating-linear-gradient(
        0deg,
        transparent,
        transparent 2px,
        rgba(0, 240, 255, 0.02) 2px,
        rgba(0, 240, 255, 0.02) 4px
      );
    pointer-events: none;
    z-index: 1;
    animation: scan-lines 8s linear infinite;
  }
}

// 赛博网格背景
.cyber-grid {
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
  z-index: 0;
  opacity: 0.3;
  
  .grid-lines-h {
    position: absolute;
    inset: 0;
    background: repeating-linear-gradient(
      0deg,
      transparent,
      transparent 50px,
      rgba(0, 240, 255, 0.1) 50px,
      rgba(0, 240, 255, 0.1) 51px
    );
    animation: grid-flow-v 20s linear infinite;
  }
  
  .grid-lines-v {
    position: absolute;
    inset: 0;
    background: repeating-linear-gradient(
      90deg,
      transparent,
      transparent 50px,
      rgba(0, 240, 255, 0.1) 50px,
      rgba(0, 240, 255, 0.1) 51px
    );
    animation: grid-flow-h 20s linear infinite;
  }
}

// 雷达扫描效果
.radar-scanner {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 300px;
  height: 300px;
  margin: -150px 0 0 -150px;
  border-radius: 50%;
  background: conic-gradient(
    from 0deg,
    transparent 0%,
    rgba(0, 240, 255, 0.1) 10%,
    transparent 20%
  );
  animation: radar-spin 4s linear infinite;
  pointer-events: none;
  z-index: 0;
  opacity: 0.3;
}

// 仪表盘风格
.monitor-dashboard {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 15px;
  padding: 15px 20px;
  position: relative;
  z-index: 2;
  
  .dashboard-item {
    position: relative;
    height: 100px;
    cursor: pointer;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    
    .dashboard-bg {
      position: absolute;
      inset: 0;
      background: linear-gradient(135deg, 
        rgba(0, 30, 60, 0.6) 0%,
        rgba(0, 50, 100, 0.4) 50%,
        rgba(0, 30, 60, 0.6) 100%
      );
      border: 1px solid rgba(0, 240, 255, 0.3);
      border-radius: 12px;
      overflow: hidden;
      transition: all 0.4s ease;
      
      .scan-line {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 100%;
        background: linear-gradient(180deg, 
          transparent 0%, 
          rgba(0, 240, 255, 0.15) 50%, 
          transparent 100%
        );
        animation: scan-vertical 3s ease-in-out infinite;
        opacity: 0;
      }
      
      .hex-pattern {
        position: absolute;
        inset: 0;
        background-image: 
          radial-gradient(circle at 25% 25%, rgba(0, 240, 255, 0.05) 2%, transparent 2%),
          radial-gradient(circle at 75% 75%, rgba(255, 0, 128, 0.05) 2%, transparent 2%);
        background-size: 30px 30px;
        background-position: 0 0, 15px 15px;
        opacity: 0.5;
      }
      
      .corner {
        position: absolute;
        width: 15px;
        height: 15px;
        border-color: rgba(0, 240, 255, 0.6);
        transition: all 0.3s ease;
        
        &.tl {
          top: -1px;
          left: -1px;
          border-top: 2px solid;
          border-left: 2px solid;
        }
        &.tr {
          top: -1px;
          right: -1px;
          border-top: 2px solid;
          border-right: 2px solid;
        }
        &.bl {
          bottom: -1px;
          left: -1px;
          border-bottom: 2px solid;
          border-left: 2px solid;
        }
        &.br {
          bottom: -1px;
          right: -1px;
          border-bottom: 2px solid;
          border-right: 2px solid;
        }
      }
    }
    
    .dashboard-content {
      position: relative;
      height: 100%;
      display: flex;
      align-items: center;
      gap: 15px;
      padding: 15px 20px;
      z-index: 1;
      
      .icon-wrapper {
        position: relative;
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        
        .icon-pulse {
          position: absolute;
          inset: -5px;
          border-radius: 50%;
          border: 2px solid rgba(0, 240, 255, 0.4);
          animation: pulse-ring 2s ease-out infinite;
        }
        
        .icon-glow {
          position: absolute;
          inset: 0;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(0, 240, 255, 0.3) 0%, transparent 70%);
          filter: blur(10px);
          animation: glow-pulse 2s ease-in-out infinite;
        }
        
        i {
          font-size: 24px;
          color: rgba(0, 240, 255, 0.8);
          position: relative;
          z-index: 1;
          transition: all 0.3s ease;
        }
        
        .icon-svg {
          width: 100%;
          height: 100%;
          color: rgba(0, 240, 255, 0.8);
          position: relative;
          z-index: 2;
          transition: all 0.3s ease;
          filter: drop-shadow(0 0 5px rgba(0, 240, 255, 0.5));
          
          circle, path, rect {
            transition: all 0.3s ease;
          }
        }
      }
      
      .info-wrapper {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: space-between;
        
        .label {
          font-size: 14px;
          color: rgba(255, 255, 255, 0.7);
          letter-spacing: 1px;
          font-weight: 500;
        }
        
        .count-wrapper {
          display: flex;
          align-items: baseline;
          gap: 5px;
          
          .count {
            font-size: 28px;
            font-weight: 700;
            font-family: 'Courier New', monospace;
            
            &.count-normal {
              color: #39FF14;
              text-shadow: 0 0 20px rgba(57, 255, 20, 0.8);
            }
            
            &.count-error {
              color: #FF2D2D;
              text-shadow: 0 0 20px rgba(255, 45, 45, 0.8);
              animation: pulse-error 2s infinite;
            }
          }
          
          .unit {
            font-size: 14px;
            color: rgba(255, 255, 255, 0.5);
          }
        }
        
        .progress-bar {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: rgba(0, 240, 255, 0.1);
          overflow: hidden;
          
          .progress-fill {
            height: 100%;
            transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
            position: relative;
            
            &.count-normal {
              background: linear-gradient(90deg, #39FF14, #2de600);
              box-shadow: 0 0 10px rgba(57, 255, 20, 0.6);
            }
            
            &.count-error {
              background: linear-gradient(90deg, #FF2D2D, #ff5555);
              box-shadow: 0 0 10px rgba(255, 45, 45, 0.6);
            }
            
            &::after {
              content: '';
              position: absolute;
              inset: 0;
              background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
              animation: shimmer 2s infinite;
            }
          }
        }
      }
    }
    
    .warning-badge {
      position: absolute;
      top: 8px;
      right: 8px;
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(255, 45, 45, 0.2);
      border: 1px solid #FF2D2D;
      border-radius: 50%;
      z-index: 2;
      
      .badge-pulse {
        position: absolute;
        inset: -4px;
        border-radius: 50%;
        border: 2px solid rgba(255, 45, 45, 0.6);
        animation: pulse-ring 1.5s ease-out infinite;
      }
      
      i {
        font-size: 12px;
        color: #FF2D2D;
        animation: pulse-warning 1.5s infinite;
        position: relative;
        z-index: 1;
      }
      
      .warning-icon {
        width: 14px;
        height: 14px;
        color: #FF2D2D;
        position: relative;
        z-index: 1;
        animation: pulse-warning 1.5s infinite;
        filter: drop-shadow(0 0 3px rgba(255, 45, 45, 0.6));
      }
    }
    
    &:hover {
      transform: translateY(-2px);
      
      .dashboard-bg {
        border-color: rgba(0, 240, 255, 0.8);
        box-shadow: 
          0 10px 40px rgba(0, 0, 0, 0.5),
          0 0 30px rgba(0, 240, 255, 0.3),
          inset 0 0 20px rgba(0, 240, 255, 0.1);
          
        .scan-line {
          opacity: 1;
        }
        
        .corner {
          border-color: #00f0ff;
          width: 20px;
          height: 20px;
        }
      }
      
      .icon-wrapper i {
        color: #00f0ff;
        transform: scale(1.1);
      }
      
      .icon-wrapper .icon-svg {
        color: #00f0ff;
        transform: scale(1.1);
        filter: drop-shadow(0 0 10px rgba(0, 240, 255, 0.8));
      }
    }
    
    &.active {
      transform: translateY(-2px);
      
      .dashboard-bg {
        background: linear-gradient(135deg, 
          rgba(0, 50, 100, 0.8) 0%,
          rgba(0, 80, 160, 0.6) 50%,
          rgba(0, 50, 100, 0.8) 100%
        );
        border-color: #00f0ff;
        box-shadow: 
          0 15px 50px rgba(0, 0, 0, 0.6),
          0 0 40px rgba(0, 240, 255, 0.5),
          inset 0 0 30px rgba(0, 240, 255, 0.2);
          
        .scan-line {
          opacity: 1;
        }
      }
      
      .icon-wrapper i {
        color: #00f0ff;
        animation: float 2s ease-in-out infinite;
      }
      
      .icon-wrapper .icon-svg {
        color: #00f0ff;
        animation: icon-rotate 3s ease-in-out infinite;
        filter: drop-shadow(0 0 15px rgba(0, 240, 255, 1));
      }
    }
  }
}

.monitor-tabs {
  display: flex;
  background: linear-gradient(135deg, rgba(0, 40, 80, 0.8), rgba(0, 21, 41, 0.9));
  border-bottom: 2px solid rgba(0, 240, 255, 0.3);
  padding: 0 20px;
  
  .tab-item {
    position: relative;
    flex: 1;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 20px;
    cursor: pointer;
    transition: all 0.3s ease;
    border-bottom: 3px solid transparent;
    
    .tab-icon {
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(0, 240, 255, 0.1);
      border-radius: 8px;
      border: 1px solid rgba(0, 240, 255, 0.3);
      
      i {
        font-size: 20px;
        color: rgba(0, 240, 255, 0.6);
      }
    }
    
    .tab-info {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 4px;
      
      .tab-label {
        font-size: 16px;
        color: rgba(255, 255, 255, 0.7);
        font-weight: 500;
      }
      
      .tab-count {
        font-size: 24px;
        font-weight: 700;
        font-family: 'Courier New', monospace;
        
        &.count-normal {
          color: #39FF14;
          text-shadow: 0 0 10px rgba(57, 255, 20, 0.5);
        }
        
        &.count-error {
          color: #FF2D2D;
          text-shadow: 0 0 10px rgba(255, 45, 45, 0.5);
          animation: pulse-error 2s infinite;
        }
      }
    }
    
    .tab-indicator {
      position: absolute;
      bottom: -3px;
      left: 0;
      right: 0;
      height: 3px;
      background: transparent;
      transition: all 0.3s ease;
    }
    
    &:hover {
      background: rgba(0, 240, 255, 0.05);
      
      .tab-icon {
        background: rgba(0, 240, 255, 0.2);
        border-color: rgba(0, 240, 255, 0.5);
        
        i {
          color: #00f0ff;
        }
      }
      
      .tab-label {
        color: #fff;
      }
    }
    
    &.active {
      background: rgba(0, 240, 255, 0.1);
      
      .tab-icon {
        background: rgba(0, 240, 255, 0.3);
        border-color: #00f0ff;
        box-shadow: 0 0 15px rgba(0, 240, 255, 0.3);
        
        i {
          color: #00f0ff;
        }
      }
      
      .tab-label {
        color: #fff;
      }
      
      .tab-indicator {
        background: linear-gradient(90deg, #00f0ff, #00d4ff);
        box-shadow: 0 0 10px rgba(0, 240, 255, 0.6);
      }
    }
  }
}

.monitor-content {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  position: relative;
  z-index: 2;
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(0, 21, 41, 0.5);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 240, 255, 0.3);
    border-radius: 4px;
    
    &:hover {
      background: rgba(0, 240, 255, 0.5);
    }
  }
}

// 数据流动背景
.data-stream {
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
  z-index: 0;
  
  .stream-line {
    position: absolute;
    top: 0;
    width: 2px;
    background: linear-gradient(180deg, transparent, rgba(0, 240, 255, 0.5), transparent);
    animation: stream-flow infinite linear;
  }
}

.error-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
  position: relative;
  z-index: 1;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 100px 20px;
  color: rgba(255, 255, 255, 0.5);
  position: relative;
  
  .empty-icon {
    position: relative;
    width: 120px;
    height: 120px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 30px;
    
    .hologram-ring {
      position: absolute;
      inset: 0;
      border: 2px solid rgba(57, 255, 20, 0.4);
      border-radius: 50%;
      animation: hologram-expand 3s ease-out infinite;
      
      &.delay-1 {
        animation-delay: 1s;
      }
      
      &.delay-2 {
        animation-delay: 2s;
      }
    }
    
    i {
      font-size: 64px;
      color: #39FF14;
      text-shadow: 0 0 30px rgba(57, 255, 20, 0.8);
      animation: float 3s ease-in-out infinite;
      position: relative;
      z-index: 1;
    }
  }
  
  .empty-text {
    font-size: 24px;
    color: #39FF14;
    font-weight: 500;
    margin-bottom: 10px;
    letter-spacing: 2px;
  }
  
  .empty-subtext {
    font-size: 12px;
    color: rgba(57, 255, 20, 0.6);
    font-family: 'Courier New', monospace;
    letter-spacing: 3px;
  }
}

.error-item {
  background: 
    linear-gradient(135deg, rgba(0, 40, 80, 0.4), rgba(0, 21, 41, 0.7)),
    linear-gradient(45deg, transparent 30%, rgba(0, 240, 255, 0.02) 50%, transparent 70%);
  border: 1px solid rgba(0, 240, 255, 0.3);
  border-radius: 0;
  clip-path: polygon(0 0, calc(100% - 10px) 0, 100% 10px, 100% 100%, 10px 100%, 0 calc(100% - 10px));
  padding: 20px;
  margin: 5px 0;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(10px);
  
  .item-border {
    position: absolute;
    inset: 0;
    pointer-events: none;
    
    .border-corner {
      position: absolute;
      width: 20px;
      height: 20px;
      border-color: rgba(0, 240, 255, 0.5);
      transition: all 0.3s ease;
      
      &.tl {
        top: 0;
        left: 0;
        border-top: 2px solid;
        border-left: 2px solid;
      }
      &.tr {
        top: 0;
        right: 0;
        border-top: 2px solid;
        border-right: 2px solid;
      }
      &.bl {
        bottom: 0;
        left: 0;
        border-bottom: 2px solid;
        border-left: 2px solid;
      }
      &.br {
        bottom: 0;
        right: 0;
        border-bottom: 2px solid;
        border-right: 2px solid;
      }
    }
  }
  
  .glitch-effect {
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent 0%, rgba(0, 240, 255, 0.05) 50%, transparent 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  .hologram-overlay {
    position: absolute;
    inset: 0;
    background: repeating-linear-gradient(
      0deg,
      transparent,
      transparent 2px,
      rgba(0, 240, 255, 0.02) 2px,
      rgba(0, 240, 255, 0.02) 4px
    );
    pointer-events: none;
  }
  
  .data-flow-line {
    position: absolute;
    left: 0;
    top: 50%;
    width: 100%;
    height: 2px;
    background: linear-gradient(90deg, 
      transparent 0%,
      rgba(0, 240, 255, 0.5) 50%,
      transparent 100%
    );
    transform: translateX(-100%);
    animation: data-flow-horizontal 3s ease-in-out infinite;
    opacity: 0;
  }
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: rgba(0, 240, 255, 0.5);
    transition: all 0.3s ease;
  }
  
  &.level-warn::before {
    background: #FFA500;
    box-shadow: 0 0 10px rgba(255, 165, 0, 0.5);
  }
  
  &.level-error::before {
    background: #FF6B6B;
    box-shadow: 0 0 10px rgba(255, 107, 107, 0.5);
  }
  
  &.level-fatal::before {
    background: #FF2D2D;
    box-shadow: 0 0 10px rgba(255, 45, 45, 0.8);
    animation: pulse-fatal 1.5s infinite;
  }
  
  &:hover {
    transform: translateX(5px);
    border-color: rgba(0, 240, 255, 0.8);
    box-shadow: 
      0 0 30px rgba(0, 240, 255, 0.3),
      inset 0 0 20px rgba(0, 240, 255, 0.05);
    
    .item-border .border-corner {
      border-color: #00f0ff;
      width: 30px;
      height: 30px;
    }
    
    .glitch-effect {
      opacity: 1;
      animation: glitch-anim 0.3s infinite;
    }
    
    .data-flow-line {
      opacity: 1;
    }
    
    &::before {
      width: 6px;
      box-shadow: 0 0 10px currentColor;
    }
  }
}

.error-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  
  .device-info {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
    
    .status-indicator {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      
      &.online {
        background: #39FF14;
        box-shadow: 0 0 10px rgba(57, 255, 20, 0.8);
        animation: pulse-online 2s infinite;
      }
      
      &.offline {
        background: #FF2D2D;
        box-shadow: 0 0 10px rgba(255, 45, 45, 0.8);
      }
    }
    
    .level-badge {
      padding: 4px 12px;
      border-radius: 4px;
      font-size: 12px;
      font-weight: 600;
      
      &.level-warn {
        background: rgba(255, 165, 0, 0.2);
        color: #FFA500;
        border: 1px solid rgba(255, 165, 0, 0.5);
      }
      
      &.level-error {
        background: rgba(255, 107, 107, 0.2);
        color: #FF6B6B;
        border: 1px solid rgba(255, 107, 107, 0.5);
      }
      
      &.level-fatal {
        background: rgba(255, 45, 45, 0.2);
        color: #FF2D2D;
        border: 1px solid rgba(255, 45, 45, 0.5);
      }
    }
    
    .device-name {
      font-size: 16px;
      font-weight: 600;
      color: #fff;
    }
    
    .device-id,
    .error-code {
      font-size: 12px;
      font-family: 'Courier New', monospace;
      color: rgba(0, 240, 255, 0.8);
      background: rgba(0, 240, 255, 0.1);
      padding: 2px 8px;
      border-radius: 4px;
    }
  }
  
  .header-actions {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  
  .error-time {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.5);
    font-family: 'Courier New', monospace;
  }
  
  .delete-btn {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 45, 45, 0.1);
    border: 1px solid rgba(255, 45, 45, 0.3);
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
    flex-shrink: 0;
    
    &::before {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(135deg, transparent, rgba(255, 45, 45, 0.2));
      opacity: 0;
      transition: opacity 0.3s ease;
    }
    
    /deep/ i {
      font-size: 18px;
      color: rgba(255, 45, 45, 0.8);
      transition: all 0.3s ease;
      position: relative;
      z-index: 1;
    }
    
    &:hover {
      background: rgba(255, 45, 45, 0.2);
      border-color: rgba(255, 45, 45, 0.6);
      box-shadow: 0 0 15px rgba(255, 45, 45, 0.3);
      transform: scale(1.05);
      
      &::before {
        opacity: 1;
      }
      
      /deep/ i {
        color: #FF2D2D;
        transform: scale(1.1);
      }
    }
    
    &:active {
      transform: scale(0.95);
    }
  }
}

.error-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
  
  .detail-item {
    display: flex;
    gap: 10px;
    
    .label {
      font-size: 14px;
      color: rgba(255, 255, 255, 0.6);
      min-width: 80px;
    }
    
    .value {
      font-size: 14px;
      color: rgba(255, 255, 255, 0.9);
      flex: 1;
      
      &.status-online {
        color: #39FF14;
      }
      
      &.status-offline {
        color: #FF2D2D;
      }
      
      &.value-highlight {
        color: #00f0ff;
        font-weight: 600;
        font-family: 'Courier New', monospace;
      }
    }
  }
}

@keyframes pulse-error {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
}

@keyframes pulse-fatal {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.4;
  }
}

@keyframes pulse-online {
  0%, 100% {
    box-shadow: 0 0 10px rgba(57, 255, 20, 0.8);
  }
  50% {
    box-shadow: 0 0 20px rgba(57, 255, 20, 1);
  }
}

@keyframes pulse-warning {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
}

@keyframes float-particle {
  0%, 100% {
    transform: translateY(0) translateX(0);
    opacity: 0.3;
  }
  50% {
    transform: translateY(-20px) translateX(10px);
    opacity: 0.8;
  }
}

@keyframes pulse-ring {
  0% {
    transform: scale(0.8);
    opacity: 1;
  }
  100% {
    transform: scale(1.4);
    opacity: 0;
  }
}

@keyframes scan-vertical {
  0% {
    transform: translateY(-100%);
  }
  100% {
    transform: translateY(100%);
  }
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

@keyframes stream-flow {
  0% {
    transform: translateY(-100%);
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
  100% {
    transform: translateY(100vh);
    opacity: 0;
  }
}

@keyframes hologram-expand {
  0% {
    transform: scale(0.8);
    opacity: 1;
  }
  100% {
    transform: scale(1.5);
    opacity: 0;
  }
}

@keyframes glitch-anim {
  0% {
    transform: translateX(0);
  }
  20% {
    transform: translateX(-2px);
  }
  40% {
    transform: translateX(2px);
  }
  60% {
    transform: translateX(-2px);
  }
  80% {
    transform: translateX(2px);
  }
  100% {
    transform: translateX(0);
  }
}

@keyframes scan-lines {
  0% {
    transform: translateY(0);
  }
  100% {
    transform: translateY(20px);
  }
}

@keyframes grid-flow-v {
  0% {
    transform: translateY(0);
  }
  100% {
    transform: translateY(50px);
  }
}

@keyframes grid-flow-h {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(50px);
  }
}

@keyframes radar-spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@keyframes glow-pulse {
  0%, 100% {
    opacity: 0.3;
    transform: scale(1);
  }
  50% {
    opacity: 0.6;
    transform: scale(1.1);
  }
}

@keyframes data-flow-horizontal {
  0% {
    transform: translateX(-100%);
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
  100% {
    transform: translateX(100%);
    opacity: 0;
  }
}

@keyframes icon-rotate {
  0%, 100% {
    transform: rotate(0deg) scale(1);
  }
  25% {
    transform: rotate(-5deg) scale(1.05);
  }
  50% {
    transform: rotate(0deg) scale(1.1);
  }
  75% {
    transform: rotate(5deg) scale(1.05);
  }
}

// 赛博朋克风格确认对话框
.cyber-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 10, 20, 0.3);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: fadeIn 0.3s ease;
}

.cyber-modal {
  width: 400px;
  background: linear-gradient(135deg, 
    rgba(0, 30, 60, 0.95) 0%,
    rgba(0, 50, 100, 0.9) 50%,
    rgba(0, 30, 60, 0.95) 100%
  );
  border: 2px solid rgba(0, 240, 255, 0.5);
  border-radius: 12px;
  position: relative;
  overflow: hidden;
  box-shadow: 
    0 0 50px rgba(0, 240, 255, 0.3),
    inset 0 0 30px rgba(0, 240, 255, 0.1);
  animation: modalSlideIn 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  
  .modal-border {
    position: absolute;
    inset: 0;
    pointer-events: none;
    
    .border-corner {
      position: absolute;
      width: 30px;
      height: 30px;
      border-color: #00f0ff;
      
      &.tl {
        top: -2px;
        left: -2px;
        border-top: 3px solid;
        border-left: 3px solid;
      }
      &.tr {
        top: -2px;
        right: -2px;
        border-top: 3px solid;
        border-right: 3px solid;
      }
      &.bl {
        bottom: -2px;
        left: -2px;
        border-bottom: 3px solid;
        border-left: 3px solid;
      }
      &.br {
        bottom: -2px;
        right: -2px;
        border-bottom: 3px solid;
        border-right: 3px solid;
      }
    }
  }
  
  .modal-scan-line {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(180deg,
      transparent 0%,
      rgba(0, 240, 255, 0.1) 50%,
      transparent 100%
    );
    animation: scan-vertical 3s ease-in-out infinite;
    pointer-events: none;
  }
  
  .modal-header {
    padding: 20px 25px;
    display: flex;
    align-items: center;
    gap: 15px;
    border-bottom: 1px solid rgba(0, 240, 255, 0.3);
    position: relative;
    
    .header-icon {
      position: relative;
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      
      .icon-pulse {
        position: absolute;
        inset: -8px;
        border-radius: 50%;
        border: 2px solid rgba(255, 165, 0, 0.6);
        animation: pulse-ring 2s ease-out infinite;
      }
      
      /deep/ i {
        color: #FFA500;
        filter: drop-shadow(0 0 10px rgba(255, 165, 0, 0.8));
        animation: pulse-warning 2s infinite;
      }
    }
    
    .header-title {
      flex: 1;
      font-size: 18px;
      font-weight: 600;
      color: #fff;
      text-shadow: 0 0 10px rgba(0, 240, 255, 0.5);
      letter-spacing: 1px;
      margin: 0;
    }
  }
  
  .modal-content {
    padding: 25px;
    position: relative;
    
    .content-text {
      font-size: 16px;
      color: rgba(255, 255, 255, 0.9);
      line-height: 1.6;
      margin: 0 0 15px 0;
    }
    
    .content-id {
      font-size: 14px;
      color: rgba(0, 240, 255, 0.8);
      font-family: 'Courier New', monospace;
      background: rgba(0, 240, 255, 0.1);
      padding: 8px 12px;
      border-radius: 4px;
      border-left: 3px solid rgba(0, 240, 255, 0.6);
      display: inline-block;
    }
  }
  
  .modal-footer {
    padding: 0 25px 20px;
    display: flex;
    gap: 12px;
    justify-content: flex-end;
    position: relative;
  }
  
  .cyber-btn {
    position: relative;
    padding: 10px 24px;
    border: none;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    overflow: hidden;
    transition: all 0.3s ease;
    
    .btn-bg {
      position: absolute;
      inset: 0;
      transition: all 0.3s ease;
    }
    
    .btn-text {
      position: relative;
      z-index: 1;
      transition: all 0.3s ease;
    }
    
    &.cancel-btn {
      border: 2px solid rgba(0, 240, 255, 0.3);
      
      .btn-bg {
        background: rgba(0, 240, 255, 0.05);
      }
      
      .btn-text {
        color: rgba(0, 240, 255, 0.9);
      }
      
      &:hover {
        border-color: rgba(0, 240, 255, 0.6);
        box-shadow: 0 0 15px rgba(0, 240, 255, 0.3);
        
        .btn-bg {
          background: rgba(0, 240, 255, 0.15);
        }
        
        .btn-text {
          color: #00f0ff;
        }
      }
    }
    
    &.confirm-btn {
      border: 2px solid rgba(255, 45, 45, 0.5);
      
      .btn-bg {
        background: rgba(255, 45, 45, 0.1);
      }
      
      .btn-text {
        color: rgba(255, 45, 45, 0.9);
      }
      
      &:hover {
        border-color: rgba(255, 45, 45, 0.8);
        box-shadow: 0 0 15px rgba(255, 45, 45, 0.4);
        transform: translateY(-1px);
        
        .btn-bg {
          background: rgba(255, 45, 45, 0.2);
        }
        
        .btn-text {
          color: #FF2D2D;
        }
      }
      
      &:active {
        transform: translateY(0);
      }
    }
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-30px) scale(0.9);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}
</style>

